import type { ReactNode } from "react";
import WaveMark from "@/assets/confluence/wave.svg?react";
import { cn } from "@/lib/utils";

const SPECS = [
  { label: "brand", value: "insight meets ambition" },
  { label: "users", value: "owners · CFOs · operators" },
  { label: "results", value: "exponential growth" },
  // { label: "how", value: "finance flows" },
] as const;

export function AuthBrandMark({ className }: { className?: string }) {
  return (
    <div className={cn("flex items-center gap-2.5", className)}>
      <span className="flex size-8 items-center justify-center rounded-[50%_50%_44%_56%] bg-gradient-to-br from-exec to-confluence text-foam">
        <WaveMark className="size-4" aria-hidden />
      </span>
      <span className="font-semibold lowercase tracking-tightest text-financial-ink">
        finance flows
      </span>
    </div>
  );
}

function AuthBrandPlane({ className }: { className?: string }) {
  return (
    <aside
      className={cn(
        "relative hidden overflow-hidden text-foam md:flex md:flex-col md:justify-between",
        "bg-[linear-gradient(118deg,#0B2732,#124653_72%,#17646E)]",
        "px-10 py-12 lg:px-14 lg:py-16",
        "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-left-3 motion-safe:duration-200",
        className,
      )}
    >
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute -right-36 -top-20 h-[190px] w-[580px]",
          "rounded-[45%_55%_62%_38%/55%_42%_58%_45%] border border-exec/20",
          "shadow-[0_24px_0_rgba(77,163,166,0.05),0_48px_0_rgba(77,163,166,0.035)]",
          "-rotate-[8deg]",
          "motion-safe:animate-in motion-safe:fade-in motion-safe:duration-500 motion-safe:delay-150",
        )}
      />

      <div className="relative z-10 max-w-md space-y-6">
        <div className="flex items-center gap-2.5 font-mono text-meta font-medium uppercase tracking-eyebrow text-shallows">
          <WaveMark className="size-5 shrink-0" aria-hidden />
          <span>where ideas converge</span>
        </div>

        <h1 className="font-sans text-5xl font-bold lowercase tracking-executive text-foam">
          finance flows
        </h1>

        <p className="max-w-sm text-base leading-relaxed text-[#BDD0D2]">
          Executive financial performance for multi-site operators — from
          location signal to portfolio outcome.
        </p>
      </div>

      <dl className="relative z-10 mt-16 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2.5 font-mono text-meta text-foam">
        {SPECS.map(({ label, value }) => (
          <div key={label} className="contents">
            <dt className="text-shallows">{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </aside>
  );
}

export interface AuthLayoutProps {
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}

export function AuthLayout({
  title,
  description,
  children,
  footer,
  className,
}: AuthLayoutProps) {
  return (
    <div className="flex min-h-svh w-full flex-col md:grid md:min-h-svh md:grid-cols-[minmax(0,1.15fr)_minmax(22rem,0.85fr)]">
      <AuthBrandPlane className="min-h-svh" />

      <div
        className={cn(
          "relative flex min-h-svh flex-1 flex-col justify-center bg-surface-solid",
          "px-6 py-10 sm:px-10 md:px-12 lg:px-16",
          "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-200",
          className
        )}
      >
        <div className="mx-auto w-full max-w-sm">
          <AuthBrandMark className="mb-8 md:hidden" />

          <header className="mb-8 space-y-2">
            <h2 className="text-2xl font-semibold tracking-tightest text-financial-ink">
              {title}
            </h2>
            {description ? (
              <p className="text-sm leading-relaxed text-slate-current">
                {description}
              </p>
            ) : null}
          </header>

          {children}

          {footer ? <div className="mt-8">{footer}</div> : null}
        </div>
      </div>
    </div>
  );
}

export function AuthLegalFooter() {
  return (
    <p className="text-balance text-center text-xs text-slate-current [&_a]:text-exec [&_a]:underline-offset-4 hover:[&_a]:underline">
      By continuing, you agree to our{" "}
      <a href="/legal/terms.html" target="_blank" rel="noopener noreferrer">
        Terms of Service
      </a>{" "}
      and{" "}
      <a href="/legal/privacy.html" target="_blank" rel="noopener noreferrer">
        Privacy Policy
      </a>
      .
    </p>
  );
}
